import {ChangeDetectionStrategy, Component, computed, input} from '@angular/core';
import {
  HarbourDuesExternalStatusInformation,
  RotterdamExternalHarbourDuesStatus,
  TaskMessageStatus
} from "@portbase/bezoekschip-service-typescriptmodels";

/**
 * BSHD-342: shows the external harbour dues status for Rotterdam of an opgave to the Zeehavengeld
 * medewerker (ZHGM), on both the ZHG overview and the ZHGM opgave.
 *
 * <p>Hybrid contract (option Y): the backend sends {@link HarbourDuesExternalStatusInformation}
 * carrying the mapped enum ({@code status}), its raw external {@code code} and the verbatim error
 * message. The two presentation mappings — the human label and the {@link TaskMessageStatus} shown
 * as a coloured status — are derived here in the frontend, keyed on the enum name. The ZHGM sees
 * "{@code code} - {@code label}" rendered through {@code app-task-message-status}; the external
 * error message (e.g. the correction remark at code 110) is surfaced as the status reason/tooltip.
 */
@Component({
  selector: 'app-harbour-dues-rotterdam-external-status',
  templateUrl: './harbour-dues-rotterdam-external-status.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HarbourDuesRotterdamExternalStatusComponent {
  externalStatus = input<HarbourDuesExternalStatusInformation>();

  // The enum name the backend sent. `status` is typed as the empty port-agnostic interface in the
  // generated model, but at runtime it is the RotterdamExternalHarbourDuesStatus enum value (its
  // bare name, e.g. "CORRECTING"), which is what both presentation maps are keyed on.
  private readonly statusName = computed(() =>
    this.externalStatus()?.status as unknown as RotterdamExternalHarbourDuesStatus);

  // "code - label" the ZHGM sees (e.g. "110 - Correcting"). The code comes from the backend; the
  // label is the frontend presentation text.
  protected readonly label = computed(() => {
    const info = this.externalStatus();
    const name = this.statusName();
    if (!info || !name) {
      return null;
    }
    return `${info.code} - ${EXTERNAL_STATUS_PRESENTATION[name].label}`;
  });

  // The coloured task status derived from the external status (BSHD-342 "Agent ziet" column).
  protected readonly taskMessageStatus = computed(() => {
    const name = this.statusName();
    return name ? EXTERNAL_STATUS_PRESENTATION[name].taskMessageStatus : TaskMessageStatus.DISABLED;
  });

  protected readonly errorMessage = computed(() => this.externalStatus()?.errorMessage);
}

// BSHD-342: the frontend presentation for each external status, keyed on the enum name. Each entry
// bundles both the human-readable label (the "description" part of "code - description") and the
// coloured task status (the story's "Agent ziet" column mapped onto TaskMessageStatus; DISABLED
// renders nothing, e.g. code 10 "No status"). Keeping label and task status on a single entry per
// status means they cannot drift apart, and a new backend status (regenerated enum) that lacks an
// entry here fails the exhaustive spec loudly rather than rendering `undefined` to the ZHGM.
interface ExternalStatusPresentation {
  label: string;
  taskMessageStatus: TaskMessageStatus;
}

const EXTERNAL_STATUS_PRESENTATION: Record<RotterdamExternalHarbourDuesStatus, ExternalStatusPresentation> = {
  [RotterdamExternalHarbourDuesStatus.NEW]: {label: "New", taskMessageStatus: TaskMessageStatus.DISABLED},
  [RotterdamExternalHarbourDuesStatus.INCOMPLETE]: {label: "Incomplete", taskMessageStatus: TaskMessageStatus.PENDING},
  [RotterdamExternalHarbourDuesStatus.OPEN]: {label: "Open", taskMessageStatus: TaskMessageStatus.SAVED},
  [RotterdamExternalHarbourDuesStatus.NON_BILLABLE]: {label: "Non billable", taskMessageStatus: TaskMessageStatus.ACCEPTED},
  [RotterdamExternalHarbourDuesStatus.IN_PROGRESS]: {label: "In progress", taskMessageStatus: TaskMessageStatus.SAVED},
  [RotterdamExternalHarbourDuesStatus.ERROR_STATUS]: {label: "Error", taskMessageStatus: TaskMessageStatus.ERROR},
  [RotterdamExternalHarbourDuesStatus.PRICING_ERROR]: {label: "Pricing Error", taskMessageStatus: TaskMessageStatus.ERROR},
  [RotterdamExternalHarbourDuesStatus.WAIT_FOR_TWO_IS_ONE]: {label: "Wait for 2=1", taskMessageStatus: TaskMessageStatus.PENDING},
  [RotterdamExternalHarbourDuesStatus.TWO_IS_ONE_APPROVED]: {label: "2=1 approved", taskMessageStatus: TaskMessageStatus.PENDING},
  [RotterdamExternalHarbourDuesStatus.NO_REVIEW_BY_PORTDUES]: {label: "No review by PortDues", taskMessageStatus: TaskMessageStatus.PENDING},
  [RotterdamExternalHarbourDuesStatus.REVIEW_BY_PORTDUES]: {label: "Review by PortDues", taskMessageStatus: TaskMessageStatus.PENDING},
  [RotterdamExternalHarbourDuesStatus.IN_PROGRESS_BACKEND]: {label: "In progress back-end", taskMessageStatus: TaskMessageStatus.PENDING},
  [RotterdamExternalHarbourDuesStatus.INVOICED]: {label: "Invoiced", taskMessageStatus: TaskMessageStatus.ACCEPTED},
  [RotterdamExternalHarbourDuesStatus.INVOICE_ERROR]: {label: "Invoice error", taskMessageStatus: TaskMessageStatus.PENDING},
  [RotterdamExternalHarbourDuesStatus.CORRECTING]: {label: "Correcting", taskMessageStatus: TaskMessageStatus.PENDING},
  [RotterdamExternalHarbourDuesStatus.CORRECTED]: {label: "Corrected", taskMessageStatus: TaskMessageStatus.ACCEPTED},
  [RotterdamExternalHarbourDuesStatus.CANCELLED]: {label: "Cancelled", taskMessageStatus: TaskMessageStatus.REJECTED},
};
